import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, HelpCircle } from "lucide-react";
import { QUICK_ANSWERS } from "./documentationOverviewContent";
import {
  SECTION_EYEBROW,
  SECTION_SPACING,
  SECTION_SUBTITLE,
  SECTION_TITLE,
} from "./overviewTokens";

export function QuickAnswersSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section aria-labelledby="doc-faq-title" className={SECTION_SPACING} id="quick-answers">
      <div className="flex items-start gap-4">
        <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
          <HelpCircle className="h-5 w-5" />
        </div>
        <div>
          <p className={SECTION_EYEBROW}>Quick answers</p>
          <h2 id="doc-faq-title" className={SECTION_TITLE}>
            The questions people ask most
          </h2>
          <p className={SECTION_SUBTITLE}>
            Short answers to common day-to-day questions. Open one to see the full reply.
          </p>
        </div>
      </div>

      <div className="mt-6 divide-y divide-slate-200 rounded-xl border border-slate-200 bg-white shadow-xs overflow-hidden">
        {QUICK_ANSWERS.map((qa, i) => {
          const isOpen = openIndex === i;
          return (
            <div key={qa.question}>
              <button
                type="button"
                onClick={() => setOpenIndex(isOpen ? null : i)}
                aria-expanded={isOpen}
                aria-controls={`faq-panel-${i}`}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors duration-200 ease-out hover:bg-slate-50"
              >
                <span className="font-heading text-sm md:text-base font-semibold text-slate-900">
                  {qa.question}
                </span>
                <ChevronDown
                  className={`h-4 w-4 flex-shrink-0 text-slate-500 transition-transform duration-300 ease-out ${
                    isOpen ? "rotate-180 text-primary" : ""
                  }`}
                />
              </button>
              <div
                id={`faq-panel-${i}`}
                className={`grid transition-all duration-300 ease-out ${
                  isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                }`}
              >
                <div className="overflow-hidden">
                  <div className="px-5 pb-5 text-sm text-slate-700 leading-relaxed">
                    <p>{qa.answer}</p>
                    {qa.link && (
                      <Link
                        to={qa.link.to}
                        className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary transition-transform duration-200 ease-out hover:gap-1.5"
                      >
                        {qa.link.label}
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
